import { State, Action, StateContext, Selector, Select, Store } from '@ngxs/store';
import { StoryChaptersStateModel } from './story.chapters.state.model';
import { 
  SetChapters,
  SelectChapter,
  NextChapter,
  PreviousChapter,
  LastChapter,
  FirstChapter,
  RemoveChapter, 
  RecordChapterVisibilityState,
  ResetStoryChapters,
  SetChapter,
  RegisterChapterThumbnails,
  TakeChapterPicture,
  TakeChapterPictureSuccess,
  RecordChapterPaintState,
  SaveLiveToChapter,
  DuplicateChapter,
  CopyThumbnail,
  RecordChapterCamera,
  RecordShadowChapter,
  SetChapterBackground,
  SetAllChaptersBackground,
  SetSelectedChapterBackground,
  ResetSelectedChapterBackground,
  RecordChapterPickableState,
  RecordChapterNodeState
} from './story.chapters.actions';

import { ChapterService } from '../../chapter.service';
import { RemoveAllChapterLabels, CopyLabels } from '../labels/story.labels.actions';
import { CameraService } from 'src/app/engine/camera.service';
import { classToClass } from 'class-transformer';
import { SnapshotService } from 'src/app/engine/snapshot.service';
import { ColorRecipeModel } from 'src/app/edit/models/color-recipe.model';
import { BdGraphService } from 'src/lib/graph/graph.service';
import { EngineState } from 'src/app/engine/state/engine.state';
import { SetVisible } from 'src/app/engine/state/engine.actions';
import { StoryChapterThumbnail } from '../../models/story-chapter-thumbnail.model';
import { cloneDeep } from 'lodash';
import * as _ from 'lodash';
import { ChapterModel } from '../../models/chapter.model';
import { BackgroundModel } from 'src/app/engine/models/background.model';
import { appDefaultBackground } from 'src/app/dialogs/components/background-color-dialog/background-config';

declare const Human: any;

const initialState = {
  loading: false,
  chapters: [],
  selectedChapter: null,
  shadowChapter: null,
  thumbnails: new Map(),
  chapterIdMap: {}
};

@State<StoryChaptersStateModel>({
  name: 'storyChapters',
  defaults: initialState
})
export class StoryChaptersState {
  /**
   * Selectors
   */
  @Selector()
  static getStoryChapters(state: StoryChaptersStateModel) {
    return state.chapters;
  }

  @Selector()
  static getSelectedChapterId(state: StoryChaptersStateModel) {
    return state.selectedChapter;
  }

  @Selector()
  static getSelectedChapter(state: StoryChaptersStateModel) {
    return state.chapters.find(chapter => chapter.id === state.selectedChapter) || null;
  }

  @Selector()
  static getSelectedChapterIndex(state: StoryChaptersStateModel) {
    return state.chapters.findIndex(chapter => chapter.id === state.selectedChapter);
  }

  @Selector()
  static getRegisteredThumbnails(state: StoryChaptersStateModel) {
    return state.thumbnails;
  }

  @Selector()
  static getShadowChapter(state: StoryChaptersStateModel) {
    return state.shadowChapter;
  }

  @Selector()
  static getChaptersLoaded(state: StoryChaptersStateModel) {
    return {
      count: state.chapters.length,
      modelChapters: state.chapters.filter(chapter => chapter.readyState.model),
      animationChapters: state.chapters.filter(chapter => chapter.readyState.animation)
    };
  }

  @Selector()
  static getAllChaptersReady(state: StoryChaptersStateModel) {
    return state.chapters.length > 0 && state.chapters.every(chapter => chapter.readyState.model);
  }

  @Selector()
  static getSelectedChapterBackground(state: StoryChaptersStateModel) {
    const { chapters, selectedChapter } = state;
    const chapter = StoryChaptersState.findChapter(chapters, selectedChapter);
    return _.get(chapter, 'background') || appDefaultBackground;
  }

  @Selector()
  static getAllChaptersBackground(state: StoryChaptersStateModel) {
    return state.chapters
      .map((chapter) => _.get(chapter, 'background'));
  }

  static findChapter(chapters: ChapterModel[], chapterId: string): ChapterModel {
    return chapters
      .find((chapter) => chapter.id === chapterId);
  }

  /**
   * Actions
   */
  @Action(SetChapters)
  setChapters(context: StateContext<StoryChaptersStateModel>, action: SetChapters) {
    context.patchState({
      chapters: [...action.chapters]
    });
  }

  @Action(SetChapter)
  setChapter(context: StateContext<StoryChaptersStateModel>, action: SetChapter) {
    const chapters = context.getState().chapters;

    const index = chapters.findIndex((_chapter) => _chapter.id === action.chapter.id);
    const chapter = chapters[index];
    
    if (JSON.stringify(chapter) !== JSON.stringify(action.chapter)) {
      chapters[index] = {...action.chapter};
      context.patchState({
        chapters: [...chapters]
      });
    }
  }

  @Action(SetChapterBackground)
  setChapterBackground(context: StateContext<StoryChaptersStateModel>, { background }) {
    const { chapters, selectedChapter } = context.getState();
    const chapter = StoryChaptersState.findChapter(chapters, selectedChapter);
    chapter.background = background;

    context.patchState({
      chapters: [...chapters]
    });
  }

  @Action(SetAllChaptersBackground)
  setAllChaptersBackground(context: StateContext<StoryChaptersStateModel>, { background }) {
    const chapters = context.getState().chapters;
    chapters.forEach((chapter) => {
      chapter.background = background;
    });

    context.patchState({
      chapters: [...chapters]
    });
  }

  @Action(ResetSelectedChapterBackground)
  resetSelectedChapterBackground(context: StateContext<StoryChaptersStateModel>, action: ResetSelectedChapterBackground) {
    const { selectedChapter, shadowChapter } = context.getState();
    this.store.dispatch(new SetChapterBackground(selectedChapter, shadowChapter.background));
  }

  @Action(SetSelectedChapterBackground)
  setSelectedChapterBackground(context: StateContext<StoryChaptersStateModel>, { background }) {
    const chapterId = context.getState().selectedChapter;
    this.store.dispatch(new SetChapterBackground(chapterId, background));
  }

  // #NOTE: [EM] Change selected chapter id and make copy for shadowChapter
  @Action(SelectChapter)
  selectChapter(context: StateContext<StoryChaptersStateModel>, action: SelectChapter) {
    this.store.dispatch(new SetVisible(action.chapter ? true : false));
    context.patchState({
      selectedChapter: action.chapter  ? action.chapter.id : null,
      shadowChapter: action.chapter  ? classToClass(action.chapter) : null
    });
  }

  // Add Shadow Chapter, which captures changes to engine state, to the Story
  @Action(SaveLiveToChapter)
  saveLiveToChapter(context: StateContext<StoryChaptersStateModel>, action: SaveLiveToChapter) {
    // identify target chapter id
    const targetChapterId = action.chapterId ? action.chapterId : context.getState().selectedChapter;
    
    // replace specified chapter with shadowChapter
    const shadowChapter = context.getState().shadowChapter;
    const chapters = context.getState().chapters;
    const index = chapters.findIndex((_chapter) => _chapter.id === targetChapterId);
    const targetChapter = {...chapters[index], ...classToClass(shadowChapter)};
    targetChapter.camera.location = this.cameraService.getCamera();
    chapters[index] = targetChapter;
    this.chapterService.replaceEngineChapterWithStateChapter(chapters[index]);
    context.patchState({
      chapters: [...chapters]
    });
  }

  @Action(DuplicateChapter)
  duplicateChapter(context: StateContext<StoryChaptersStateModel>, action: DuplicateChapter) {
    // create a safe chapter copy
    const copyFromShadow = action.chapter ? false : true;
    const sourceChapter = copyFromShadow ? context.getState().shadowChapter : action.chapter;
    const targetChapter = this.chapterService.copyChapter(sourceChapter);
    
    // insert chapter copy into ENGINE
    const chapters = context.getState().chapters;
    const insertIndex = action.location ? Math.min(action.location, chapters.length) : chapters.length;
    
    // #NOTE: [EM] deep copy is important. Otherwise engine replaces timeline map object.
    this.chapterService.addChapterToEngine(cloneDeep(targetChapter), insertIndex); 

    // update application STATE
    chapters[insertIndex] = targetChapter;
    context.patchState({
      chapters: [...chapters]
    });
    context.dispatch(new CopyLabels(sourceChapter, targetChapter));
    context.dispatch(new CopyThumbnail(copyFromShadow ? null : sourceChapter, targetChapter));
  }

  @Action(CopyThumbnail)
  copyThumbnail(context: StateContext<StoryChaptersStateModel>, action: CopyThumbnail) {
    // copy thumbnail or take new snapshot for target chapter
    const thumbnails = context.getState().thumbnails;
    let targetThumbnail = new StoryChapterThumbnail();
    if (action.sourceChapter) {
      // copy, and point snapshot to original large url
      const sourceThumbnail = thumbnails.get(action.sourceChapter.id);

      targetThumbnail = classToClass(sourceThumbnail);
      targetThumbnail.urls.small = targetThumbnail.urls.small.replace(action.sourceChapter.id, action.targetChapter.id);
      targetThumbnail.urls.large = targetThumbnail.urls.large.replace(action.sourceChapter.id, action.targetChapter.id);
      if (sourceThumbnail.snapshot) {
        targetThumbnail.snapshot =  sourceThumbnail.snapshot;
      } else {
        targetThumbnail.snapshot = thumbnails.get(action.sourceChapter.id).urls.large;
      }
      
      thumbnails.set(action.targetChapter.id, targetThumbnail);
      context.patchState({
        thumbnails: new Map(thumbnails)
      });

    } else {
      // take a snapshot
      context.dispatch(new TakeChapterPicture(action.targetChapter.id));
    }
  }

  @Action(NextChapter)
  nextChapter(context: StateContext<StoryChaptersStateModel>, action: NextChapter) {
    const chapters = context.getState().chapters;
    const currentChapterId = this.chapterService.getCurrentChapterId();
    let index = 0;
    index = chapters.findIndex(chapter => chapter.id === currentChapterId);
    if (index < chapters.length - 1) {
      index++;
    }

    context.dispatch(new SelectChapter(chapters[index]));    
  }

  @Action(PreviousChapter)
  previousChapter(context: StateContext<StoryChaptersStateModel>, action: PreviousChapter) {
    const chapters = context.getState().chapters;
    const currentChapterId = this.chapterService.getCurrentChapterId();
    let index = 0;
    index = chapters.findIndex(chapter => chapter.id === currentChapterId);
    if (index > 0) {
      index--;
    }

    context.dispatch(new SelectChapter(chapters[index]));    
  }

  @Action(LastChapter)
  lastChapter(context: StateContext<StoryChaptersStateModel>, action: LastChapter) {
    const chapters = context.getState().chapters;
    
    context.dispatch(new SelectChapter(chapters[chapters.length - 1]));
  }

  @Action(FirstChapter)
  firstChapter(context: StateContext<StoryChaptersStateModel>, action: LastChapter) {
    const chapters = context.getState().chapters;
    
    context.dispatch(new SelectChapter(chapters[0]));
  }
    
  @Action(RemoveChapter)
  removeChapter(context: StateContext<StoryChaptersStateModel>, action: RemoveChapter) {
    const { chapters, selectedChapter } = context.getState();

    // Don't allow the user to remove the last chapter,
    // or else the system will break. 
    if (chapters.length === 1) {
      return;
    }

    // Trigger a rebuild of the chapter object in the engine before updating the application. 
    this.chapterService.removeChapterFromEngine(action.chapter);

    // Update chapters array
    let chapterIndex = chapters.findIndex(chapter => chapter.id === action.chapter.id);
    chapters.splice(chapterIndex, 1);
    
    context.patchState({
      chapters: [...chapters]
    });

    // Selected chapter deleted, need to select a new chapter by default
    if (action.chapter.id === selectedChapter) {

      // Make sure selected chapter doesn't exceed chapter array size
      if (chapterIndex === chapters.length) {
        chapterIndex--;
      }

      context.dispatch(new SelectChapter(chapters[chapterIndex]));
    }

    // Nuke all chapter labels
    context.dispatch(new RemoveAllChapterLabels(action.chapter));
  }
  
  @Action(ResetStoryChapters)
  resetStoryChaptersState(context: StateContext<StoryChaptersStateModel>, action: ResetStoryChapters) {
    context.setState(initialState);
  }

  @Action(RecordShadowChapter)
  recordShadowChapter(context: StateContext<StoryChaptersStateModel>, action: RecordShadowChapter) {
    context.patchState({
      shadowChapter: classToClass(action.chapter)
    });
  }
  

  @Action(RecordChapterVisibilityState)
  recordChapterVisibilityState(context: StateContext<StoryChaptersStateModel>, action: RecordChapterVisibilityState) {
    this.store.dispatch(new RecordChapterNodeState(action.nodes, { 'shown': action.visible }, action.record));
  }

  // record chagnes to shadowObject and conditionally to current chapter
  @Action(RecordChapterPaintState)
  recordChapterPaintState(context: StateContext<StoryChaptersStateModel>, action: RecordChapterPaintState) {
    // get shadow chapter
    const shadowChapter = context.getState().shadowChapter;
    
    // get view graph and filter nodes for subsumption (deeper nodes subsumed by shallower nodes)
    const viewGraph = this.store.selectSnapshot(EngineState.getViewGraph);
    const accessorIds: string[] = this.graphService.filterGraphAccessors(viewGraph, action.nodes.map((node) => node.id));

    // update the state record of chapter. Clear color mods beneath accessors, so they do not block this paint state
    let chapterSync = shadowChapter.synchronization;
    accessorIds.forEach((id) => {
      // clear color mods on deeper nodes in the graph
      chapterSync = this.chapterService.clearColorModsFromSync(chapterSync, this.graphService.getDescendants(viewGraph, id));
    
      // set the synchronization on the subsuming accessor
      chapterSync[id] = {...chapterSync[id] || {}, ...ColorRecipeModel.toEngine(action.paintState)};
    });

    // update the engine's record
    shadowChapter.synchronization = {...chapterSync}; // needs new object

    // patch state with updated stateful chapter record
    context.patchState({
      shadowChapter: classToClass(shadowChapter)
    });

    // conditionally save with current story chapter
    if (action.record) {
      const chapters = context.getState().chapters;
      const currentChapterId = context.getState().selectedChapter;
      const currentChapterIndex = chapters.findIndex((_chapter) => _chapter.id === currentChapterId);
      const chapter = chapters[currentChapterIndex];
      
      this.chapterService.updateChapterSynchronization(chapter.id, shadowChapter.synchronization);
      chapter.synchronization = {...shadowChapter.synchronization};
      context.patchState({
        chapters: [...chapters]
      });
    }

  }

  @Action(RecordChapterCamera)
  recordChapterCamera(context: StateContext<StoryChaptersStateModel>, action: RecordChapterCamera) {
    // get shadow chapter
    const shadowChapter = context.getState().shadowChapter;
    
    // record change on shadow chapter
    shadowChapter.camera.location = action.camera;

    // patch state with updated stateful chapter record
    context.patchState({
      shadowChapter: classToClass(shadowChapter)
    });

    // conditionally save with current story chapter
    if (action.record) {
      const chapters = context.getState().chapters;
      const currentChapterId = context.getState().selectedChapter;
      const currentChapterIndex = chapters.findIndex((_chapter) => _chapter.id === currentChapterId);
      const chapter = chapters[currentChapterIndex];
      
      this.chapterService.updateChapterSynchronization(chapter.id, shadowChapter.synchronization);
      chapter.camera.location = {...shadowChapter.camera.location};
      context.patchState({
        chapters: [...chapters]
      });
    }

  }

  @Action(RecordChapterPickableState)
  recordChapterPickableState(context: StateContext<StoryChaptersStateModel>, action: RecordChapterPickableState) {
    this.store.dispatch(new RecordChapterNodeState(action.nodes, { 'pickable': action.pickable }, action.record));
  }

  @Action(RecordChapterNodeState)
  recordChapterNodeState(context: StateContext<StoryChaptersStateModel>, action: RecordChapterNodeState) {
    // get shadow chapter
    const shadowChapter = context.getState().shadowChapter;

    // get view graph and filter nodes for subsumption (deeper nodes subsumed by shallower nodes)
    const viewGraph = this.store.selectSnapshot(EngineState.getViewGraph);
    const accessorIds: string[] = this.graphService.filterGraphAccessors(viewGraph, action.nodes.map((node) => node.id));

    // update shadow chapter synchronzation
    let chapterSync = shadowChapter.synchronization;
    accessorIds.forEach((id) => {

      Object.entries(action.stateMap).forEach(([propName, propValue]) => { // i.e., { visible: true, pickable: false }
        // clear color mods on deeper nodes in the graph
        chapterSync = this.chapterService.clearBoolStateFromSync(chapterSync, this.graphService.getDescendants(viewGraph, id), propName);

        // set the synchronization on the subsuming accessor
        chapterSync[id] = { ...chapterSync[id] || {}, ...{ [propName]: propValue } };
      });

    });

    // patch state with updated stateful chapter record
    context.patchState({
      shadowChapter: classToClass(shadowChapter)
    });

    // conditionally save with current story chapter
    if (action.record) {
      const chapters = context.getState().chapters;
      const currentChapterId = context.getState().selectedChapter;
      const currentChapterIndex = chapters.findIndex((_chapter) => _chapter.id === currentChapterId);
      const chapter = chapters[currentChapterIndex];

      this.chapterService.updateChapterSynchronization(chapter.id, shadowChapter.synchronization);
      chapter.synchronization = { ...shadowChapter.synchronization };
      context.patchState({
        chapters: [...chapters]
      });
    }
  }

  @Action(RegisterChapterThumbnails)
  registerChapterThumbnails(context: StateContext<StoryChaptersStateModel>, action: RegisterChapterThumbnails) {
    // urls that came with scene for managing and housekeeping of server thumbnails per chapter
    context.patchState({
      thumbnails: new Map(action.thumbnails)
    });
  }

  @Action(TakeChapterPicture)
  takeChapterSnapshot(context: StateContext<StoryChaptersStateModel>, action: TakeChapterPicture) {
    const selectedBackground = this.store.selectSnapshot(StoryChaptersState.getSelectedChapterBackground);
    const snapshotBackground = selectedBackground && !BackgroundModel.isTransparent(selectedBackground) ? selectedBackground : appDefaultBackground;

    this.snapshotService.takeSnapshot(action.width, action.height, snapshotBackground).subscribe((blob) => {
      context.dispatch(new TakeChapterPictureSuccess(action.chapterId, blob));
    });
  }

  @Action(TakeChapterPictureSuccess)
  takeChapterSnapshotSuccess(context: StateContext<StoryChaptersStateModel>, action: TakeChapterPictureSuccess) {
    const thumbnails = context.getState().thumbnails;

    const thumbnail = thumbnails.get(action.chapterId) || new StoryChapterThumbnail();

    thumbnail.snapshot = URL.createObjectURL(action.blob);
    thumbnails.set(action.chapterId, thumbnail);
    
    context.patchState({
      thumbnails: new Map(thumbnails)
    });
  }
  
  constructor(
    private chapterService: ChapterService,
    private cameraService: CameraService,
    private snapshotService: SnapshotService,
    private store: Store,
    private graphService: BdGraphService
  ) { }
}
